import React from "react";
import dayjs from "dayjs";
import {IdcardOutlined, MinusCircleOutlined, PlusCircleOutlined} from "@ant-design/icons";
import {Button, ButtonVariants, DatePicker, DatePickerSizes, Input, InputSizes, Select, SelectSizes} from "@clockout-dev/design-elements";
import ComposerSection from "./ComposerSection";
import {PHONE_TYPE_OPTIONS} from "../uploads-simulator.constants";
import {buildEmptyPhoneRow} from "../sor.model";
import styles from "../styles/sor-composer.module.scss";

/**
 * SOR identity section: name / DOB / last-4 / emails / phones / address.
 * Emails are edited as a comma-separated line (stored as an array); phone
 * numbers are typed rows. Props-only — the controller owns the SOR object.
 *
 * @param {Object} props
 * @param {Object} props.identity — `sor.identity`
 * @param {Record<string, string>} [props.errors]
 * @param {boolean} [props.filled]
 * @param {(identity: Object) => void} props.onChange
 */
function SorIdentitySection({identity, errors, filled, onChange}) {
    const value = identity || {};
    const setField = (field, fieldValue) => onChange({...value, [field]: fieldValue});
    const setAddressField = (field, fieldValue) => onChange({...value, address: {...(value.address || {}), [field]: fieldValue}});
    const setPhone = (index, patch) =>
        setField(
            "phone_numbers",
            (value.phone_numbers || []).map((row, rowIndex) => (rowIndex === index ? {...row, ...patch} : row))
        );

    return (
        <ComposerSection icon={<IdcardOutlined />} title="Identity" filled={filled} defaultCollapsed>
            <div className={styles.fieldGrid}>
                <Input label="Full name" size={InputSizes.MEDIUM} placeholder="Jordan Sample" value={value.full_name || ""} onChange={event => setField("full_name", event.target.value)} />
                <DatePicker
                    label="Date of birth"
                    size={DatePickerSizes.MEDIUM}
                    className={styles.dateField}
                    value={value.date_of_birth ? dayjs(value.date_of_birth) : null}
                    onChange={date => setField("date_of_birth", date ? date.format("YYYY-MM-DD") : "")}
                />
                <Input
                    label="Last 4 of SSN"
                    size={InputSizes.MEDIUM}
                    maxLength={4}
                    placeholder="1234"
                    status={errors?.["identity.last_four_ssn"] ? "error" : ""}
                    value={value.last_four_ssn || ""}
                    onChange={event => setField("last_four_ssn", event.target.value.replace(/\D/g, ""))}
                />
                <Input
                    label="Emails (comma-separated)"
                    size={InputSizes.MEDIUM}
                    placeholder="jordan@example.com"
                    value={(value.emails || []).join(", ")}
                    onChange={event =>
                        setField(
                            "emails",
                            event.target.value
                                .split(",")
                                .map(email => email.trim())
                                .filter((email, index, list) => email || index < list.length - 1)
                        )
                    }
                />
            </div>

            {(value.phone_numbers || []).map((row, index) => (
                <div key={index} className={styles.inlineRow}>
                    <Select
                        label={index === 0 ? "Phone type" : null}
                        size={SelectSizes.MEDIUM}
                        className={styles.inlineSelect}
                        options={PHONE_TYPE_OPTIONS}
                        value={row.type || "mobile"}
                        onChange={type => setPhone(index, {type})}
                    />
                    <Input
                        label={index === 0 ? "Phone number" : null}
                        size={InputSizes.MEDIUM}
                        placeholder="5555550123"
                        value={row.value || ""}
                        onChange={event => setPhone(index, {value: event.target.value})}
                    />
                    <Button
                        variant={ButtonVariants.TEXT}
                        className={styles.removeRowButton}
                        aria-label="Remove phone"
                        onClick={() =>
                            setField(
                                "phone_numbers",
                                (value.phone_numbers || []).filter((_, rowIndex) => rowIndex !== index)
                            )
                        }>
                        <MinusCircleOutlined />
                    </Button>
                </div>
            ))}
            <Button variant={ButtonVariants.TEXT} className={styles.addRowLink} onClick={() => setField("phone_numbers", [...(value.phone_numbers || []), buildEmptyPhoneRow()])}>
                <PlusCircleOutlined />
                Add a phone number
            </Button>

            <div className={styles.fieldGrid}>
                <Input label="Address line 1" size={InputSizes.MEDIUM} value={value.address?.line1 || ""} onChange={event => setAddressField("line1", event.target.value)} />
                <Input label="Address line 2" size={InputSizes.MEDIUM} value={value.address?.line2 || ""} onChange={event => setAddressField("line2", event.target.value)} />
                <Input label="City" size={InputSizes.MEDIUM} value={value.address?.city || ""} onChange={event => setAddressField("city", event.target.value)} />
                <Input
                    label="State"
                    size={InputSizes.MEDIUM}
                    maxLength={2}
                    placeholder="NY"
                    value={value.address?.state || ""}
                    onChange={event => setAddressField("state", event.target.value.toUpperCase())}
                />
                <Input label="Postal code" size={InputSizes.MEDIUM} value={value.address?.postal_code || ""} onChange={event => setAddressField("postal_code", event.target.value)} />
                <Input label="Country" size={InputSizes.MEDIUM} value={value.address?.country || ""} onChange={event => setAddressField("country", event.target.value)} />
            </div>
        </ComposerSection>
    );
}

export default SorIdentitySection;
